import React from "react";
import { getRegistration } from "./registry";
import type { DataLoader, LoaderContext, ChatMessage, WidgetComponent } from "../types";
import toLower from "lodash/toLower";

export async function runLoaders<T extends ChatMessage>(
  base: T,
  loaders: DataLoader<T, T>[],
  ctx?: LoaderContext
): Promise<T> {
  let current = base;
  for (const loader of loaders) {
    current = await loader.load(current, ctx);
  }
  return current;
}

export async function renderMessages<T extends ChatMessage>(
  messages: T[],
  ctx?: LoaderContext
): Promise<React.ReactNode[] | null[]> {
  try {
    return await Promise.all(messages.map(message => renderMessage(message, ctx)));
  } catch (error) {
    console.error(error);
    return [];
  }
}

export async function renderMessage<T extends ChatMessage>(
  message: T,
  ctx?: LoaderContext
): Promise<React.ReactNode | null> {
  try {
    const registration = getRegistration(toLower(message.type));
    if (!registration?.component) {
      console.warn(`No component registered for message type: ${message.type}`);
      return null;
    }

    // Create loader promise if dataLoader exists
    // Components can handle the promise internally to show loading states
    const loaderPromise = registration.dataLoader
      ? runLoaders(message, [registration.dataLoader as unknown as DataLoader<ChatMessage, ChatMessage>], ctx)
      : undefined;

    // Cast to WidgetComponent since we're passing message-based props
    // WidgetComponent expects { message, data?, loaderPromise?, onAction? }
    const Component = registration.component as WidgetComponent<any>;
    return React.createElement(Component, { message, loaderPromise });
  } catch (error) {
    console.error(error);
    return null;
  }
}

